import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import EmptyState from '../components/CommonComponents/EmptyState';
import { BarChart3, ShoppingCart, AlertCircle, Clock } from 'lucide-react';
import BranchSelector from '../components/admin/analytics/BranchSelector';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const API_URL = `${process.env.REACT_APP_API_URL}/api/admin/orders/analytics`;

// add a constant for low-stock threshold
const LOW_STOCK_THRESHOLD = parseInt(process.env.REACT_APP_LOW_STOCK_THRESHOLD) || 10;

// Utility function to format currency
const formatCurrency = (amount) => {
  if (isNaN(amount)) return "₱0.00";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount);
};

export default function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preReleaseRequests, setPreReleaseRequests] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [totalLowStockCount, setTotalLowStockCount] = useState(0);
  const [branchId, setBranchId] = useState("");
  const [branchName, setBranchName] = useState("All Branches");

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const customerId = sessionStorage.getItem("customer_id") || localStorage.getItem("customer_id");

      let url = API_URL;
      const params = [];
      if (customerId) params.push(`customer_id=${customerId}`);
      if (branchId) params.push(`branch_id=${branchId}`);
      params.push(`tzOffset=${new Date().getTimezoneOffset()}`);
      if (params.length) url += `?${params.join("&")}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  // Fetch pre-release requests
  const fetchPreReleases = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const branchParam = branchId ? `&branch_id=${branchId}` : "";
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/pre-releases?page=1&pageSize=3&status=PENDING${branchParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setPreReleaseRequests(data.data || []);
    } catch (err) {
      console.error("Failed to fetch pre-releases:", err);
    }
  }, [branchId]);

  // Fetch low stock items
  const fetchLowStock = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/inventory/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const all = Array.isArray(data) ? data : [];
      const lowStock = all.filter(item => item.product_id && (item.quantity ?? 0) <= LOW_STOCK_THRESHOLD);
      const branchFiltered = branchId
        ? lowStock.filter(item => Number(item.branch_id) === Number(branchId))
        : lowStock.filter(item => item.branch_id !== null && item.branch_id !== undefined);

      setTotalLowStockCount(branchFiltered.length);
      setLowStockItems(branchFiltered.slice(0, 5));
    } catch (err) {
      console.error("Failed to fetch low stock:", err);
    }
  }, [branchId]);

  useEffect(() => {
    fetchAnalytics();
    fetchPreReleases();
    fetchLowStock();
  }, [fetchAnalytics, fetchPreReleases, fetchLowStock]);

  if (error) {
    return (
      <div className="p-6">
        <EmptyState
          icon={BarChart3}
          title="Failed to Load Dashboard"
          message="Check your internet connection and try again."
          onRetry={fetchAnalytics}
          retryLabel="Retry"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div className="h-8 w-40 bg-gray-200 rounded animate-pulse" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(3)].map((_, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow p-4 border-l-4 border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                  <div className="mt-2 h-6 w-28 bg-gray-100 rounded animate-pulse" />
                  <div className="mt-2 h-3 w-20 bg-gray-100 rounded animate-pulse" />
                </div>
                <div className="h-10 w-10 bg-gray-100 rounded-full animate-pulse" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="h-60 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="space-y-3">
              {[...Array(5)].map((_, idx) => (
                <div key={idx} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <div className="h-3 w-36 bg-gray-100 rounded animate-pulse" />
                    <div className="mt-2 h-3 w-24 bg-gray-100 rounded animate-pulse" />
                  </div>
                  <div className="h-5 w-10 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="space-y-3">
              {[...Array(4)].map((_, idx) => (
                <div key={idx} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <div className="h-3 w-32 bg-gray-100 rounded animate-pulse" />
                    <div className="mt-2 h-3 w-24 bg-gray-100 rounded animate-pulse" />
                  </div>
                  <div className="h-5 w-14 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const normalizeDate = (dateValue) => {
    if (!dateValue) return null;
    const cleaned = String(dateValue).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      const [year, month, day] = cleaned.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(cleaned)) {
      const [year, month, day] = cleaned.slice(0, 10).split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(cleaned);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  // Calculate metrics
  // Always match today's sales by local date string
  const todayDateLocal = new Date().toLocaleDateString('en-CA').trim();
  let today = { totalSales: 0, ordersCount: 0 };
  if (analytics?.salesByDay?.length) {
    const found = analytics.salesByDay.find(d => {
      const dt = normalizeDate(d.date);
      return dt && dt.toLocaleDateString('en-CA') === todayDateLocal;
    });
    if (found) today = found;
  }

  // Helper to filter by date range
  // Filter by date range using local time zone
  const filterByDateRange = (data, days) => {
    if (!Array.isArray(data)) return [];
    const today = new Date();
    return data.filter(d => {
      const dt = normalizeDate(d.date);
      if (!dt) return false;
      const diff = (today - dt) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff < days;
    });
  };

  // This month: filter by current month
  const now = new Date();
  const thisMonth = (analytics?.salesByDay || []).filter(d => {
    const dt = normalizeDate(d.date);
    return dt && dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
  });
  const totalSalesMonth = thisMonth.reduce((sum, d) => sum + parseFloat(d.totalSales), 0);
  const totalOrdersMonth = thisMonth.reduce((sum, d) => sum + parseInt(d.ordersCount), 0);

  // Last 7 days: filter by date
  const last7Days = filterByDateRange(analytics?.salesByDay, 7);
  // Sort by date ascending for chart
  const sorted7Days = [...last7Days].sort((a, b) => {
    const da = normalizeDate(a.date);
    const db = normalizeDate(b.date);
    if (!da || !db) return 0;
    return da - db;
  });
  const chartLabels = sorted7Days.map(d => {
    const dt = normalizeDate(d.date);
    return dt ? dt.toLocaleDateString('en-CA') : '';
  });
  const chartData = sorted7Days.map(d => parseFloat(d.totalSales));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <BranchSelector
          value={branchId}
          onChange={setBranchId}
          onSelect={(opt) => setBranchName(opt?.label || "All Branches")}
        />
      </div>

      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Today's Sales */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Today's Sales</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(today.totalSales)}</p>
              <p className="text-xs text-gray-400 mt-1">{today.ordersCount} transactions</p>
            </div>
            <ShoppingCart className="w-8 h-8 text-blue-500 opacity-20" />
          </div>
        </div>

        {/* Monthly Sales */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSalesMonth)}</p>
              <p className="text-xs text-gray-400 mt-1">{totalOrdersMonth} orders</p>
            </div>
            <BarChart3 className="w-8 h-8 text-green-500 opacity-20" />
          </div>
        </div>

        {/* Removed Avg Order Value card for best practice */}

        {/* Low Stock Alert */}
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Low Stock</p>
              <p className="text-2xl font-bold text-gray-900">{totalLowStockCount}</p>
              <Link to="/admin/inventory" className="text-xs text-blue-600 hover:underline mt-1 block">
                View inventory →
              </Link>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Row 2: Charts & Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Sales Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Sales Trend (Last 7 Days)</h2>
            <Link to="/admin/analytics" className="text-xs text-blue-600 hover:underline">
              View full →
            </Link>
          </div>
          <div className="h-60">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Sales',
                    data: chartData,
                    borderColor: '#6366F1',
                    backgroundColor: 'rgba(99,102,241,0.08)',
                    pointBackgroundColor: '#6366F1',
                    pointRadius: 4,
                    tension: 0.4,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { callback: v => `₱${v}` },
                  },
                },
              }}
            />
          </div>
        </div>
        {/* Today's Sales by Category */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Today's Sales by Category</h2>
            <Link to="/admin/transactions" className="text-xs text-blue-600 hover:underline">
              View details →
            </Link>
          </div>
          <div className="text-xs text-gray-500 mb-3">
            {branchName}
          </div>
          {Array.isArray(analytics?.categorySalesToday) && analytics.categorySalesToday.length > 0 ? (
            <div className="space-y-3">
              {analytics.categorySalesToday.slice(0, 6).map((row, idx) => (
                <div key={idx} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{row.category || "Uncategorized"}</p>
                    <p className="text-xs text-gray-500">{Number(row.totalQty || 0)} items</p>
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{formatCurrency(Number(row.totalSales || 0))}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No sales for today.</p>
          )}
        </div>

      </div>

      {/* Row 3: Pending Requests & Low Stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Pre-Release Requests */}
        {/* <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Pending Pre-Release ({preReleaseRequests.length})
            </h2>
            <Link to="/admin/pre-release-tracker" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          {preReleaseRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No pending requests</p>
          ) : (
            <div className="space-y-3">
              {preReleaseRequests.map((pr) => (
                <div key={pr.id} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{pr.branch?.name}</p>
                    <p className="text-xs text-gray-500">{pr.items?.length || 0} items</p>
                  </div>
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded">
                    {pr.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div> */}
        
        {/* Top Products */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Top Products</h2>
            <Link to="/admin/analytics" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {(analytics?.topProducts || []).slice(0, 5).map((product, idx) => (
              <div key={idx} className="flex items-center justify-between pb-3 border-b">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {product.product || `Product ${product.product_id}`}
                  </p>
                  <p className="text-xs text-gray-500">{product.totalQty} units sold</p>
                </div>
                <div className="text-sm font-semibold text-gray-900">{product.totalQty}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Low Stock Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Low Stock Items ({totalLowStockCount})
            </h2>
            <Link to="/admin/inventory" className="text-xs text-blue-600 hover:underline">
              View all →
            </Link>
          </div>
          {lowStockItems.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">All items well stocked</p>
          ) : (
            <div className="space-y-3">
              {lowStockItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between pb-3 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {item.product_name || item.product?.name || item.name || `Product ${item.id}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.branch?.name || item.branch_name || item.branch || "Unknown Branch"}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${item.quantity <= 3
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {item.quantity} left
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
